import type { ServiceDiagnosticsSection } from '@web/vin-sessions/types';
import type { StyleProp } from 'react-native/Libraries/StyleSheet/StyleSheet';
import type { TextStyle } from 'react-native/Libraries/StyleSheet/StyleSheetTypes';

import { colors } from '@packages/core/styles';
import { Button, Icon, RobotoFontFamily, Typography } from '@packages/ui/shared';
import { SkeletonLoader } from '@web/components/skeleton-loader';
import { Share } from '@web/vin-sessions/assets/Icons';
import { KeywordPreview } from '@web/vin-sessions/components/stis-publications/document-preview-modal';
import { type FC, type ReactNode, type UIEvent } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import { ActionIconButton } from './action-icon-button';

type Props = {
    isLoading: boolean;
    manualTitle: string;
    onClose: () => void;
    onLoadMore: () => void;
    onOpenManual: () => void;
    onOpenSection: (section: ServiceDiagnosticsSection) => void;
    onShareSection: (section: ServiceDiagnosticsSection) => void;
    query: string;
    sections: ServiceDiagnosticsSection[];
    show: boolean;
    totalElements: number;
};

const typographyStyles = {
    modalHeaderStyle: {
        fontFamily: 'Roboto',
        fontSize: 15,
        fontWeight: 600,
        letterSpacing: 0,
        lineHeight: 16,
    } as unknown as StyleProp<TextStyle>,
    paragraphStyle: {
        color: '#4C525D',
        display: 'inline-block',
        fontFamily: RobotoFontFamily.Regular,
        fontSize: 13,
        fontWeight: 400,
        marginBottom: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
    } as React.CSSProperties,
};

const BODY_STYLE = { maxHeight: '472px', overflowX: 'hidden', overflowY: 'auto' } as const;

const CARD_STYLE = { border: '1px solid #999DA3', cursor: 'pointer' } as const;

const CARD_TEXT_STYLE = {
    display: 'flex',
    flex: '1 1 auto',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
} as const;

const CRUMB_STYLE = {
    color: '#071424',
    fontSize: 14,
    fontWeight: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
} as const;

const useStyles = createUseStyles({
    hit: {
        '& b': {
            color: 'inherit',
            fontWeight: 700,
        },
    },
});

const CARD_ACTIONS_STYLE = {
    alignItems: 'center',
    display: 'flex',
    flex: '0 0 auto',
    gap: 5,
    paddingLeft: 20,
} as const;

const CANCEL_STYLE = { marginRight: 5 } as const;

const SKELETON_ROW_KEYS = Array.from({ length: 6 }, (_, index) => `sd-preview-skeleton-${index}`);

const SKELETON_MORE_KEYS = SKELETON_ROW_KEYS.slice(0, 2).map((key) => `${key}-more`);

const SKELETON_CARD_STYLE = { border: '1px solid #E3E3E3' } as const;

const skeletonRows = (keys: string[]): ReactNode =>
    keys.map((key) => (
        <div
            className="d-flex justify-content-between align-items-center mb-2 mt-4 rounded p-2"
            key={key}
            style={SKELETON_CARD_STYLE}
        >
            <div style={CARD_TEXT_STYLE}>
                <SkeletonLoader height={14} type="text" width="45%" />
                <SkeletonLoader height={13} type="text" width="90%" />
            </div>
            <div style={CARD_ACTIONS_STYLE}>
                <SkeletonLoader type="icon" />
            </div>
        </div>
    ));

const sectionPath = (section: ServiceDiagnosticsSection): string =>
    section.titlePath ? `${section.title} > ${section.titlePath}` : section.title;

const highlightPath = (path: string, query: string): ReactNode => {
    const words = query
        .split(/\s+/)
        .map((word) => word.trim())
        .filter((word) => word.length > 1)
        .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (words.length === 0) {
        return path;
    }
    const pieces = path.split(new RegExp(`(${words.join('|')})`, 'gi'));
    const matches = new RegExp(`^(?:${words.join('|')})$`, 'i');
    return pieces.map((piece, index) => (matches.test(piece) ? <b key={`${piece}-${index}`}>{piece}</b> : piece));
};

export const SdPreviewModal: FC<Props> = ({
    isLoading,
    manualTitle,
    onClose,
    onLoadMore,
    onOpenManual,
    onOpenSection,
    onShareSection,
    query,
    sections,
    show,
    totalElements,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const openLabel = t('stisPublications:serviceDiagnostics.openPage', 'Open this page');
    const shareLabel = t('stisPublications:table.share', 'Share');

    return (
        <Modal centered onHide={onClose} show={show} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {t('stisPublications:serviceDiagnostics.keywordPreviews', 'Keyword Previews')}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body
                onScroll={(event: UIEvent<HTMLDivElement>) => {
                    const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
                    // A little before the end, so the next page is there by the time it is reached
                    if (scrollTop + clientHeight >= scrollHeight - 48) {
                        onLoadMore();
                    }
                }}
                style={BODY_STYLE}
            >
                <Typography style={typographyStyles.modalHeaderStyle}>
                    {t('stisPublications:serviceDiagnostics.previewResults', {
                        count: totalElements,
                        defaultValue: '{{count}} Results for "{{query}}" in {{title}}',
                        query,
                        title: manualTitle,
                    })}
                </Typography>

                {isLoading && sections.length === 0 && (
                    <div
                        aria-label={t('stisPublications:serviceDiagnostics.previewLoading', 'Finding the pages…')}
                        aria-live="polite"
                        role="status"
                    >
                        {skeletonRows(SKELETON_ROW_KEYS)}
                    </div>
                )}

                {!isLoading && sections.length === 0 && (
                    <Typography variant="caption">
                        {t(
                            'stisPublications:serviceDiagnostics.previewEmpty',
                            'No pages in this manual contain the keyword.'
                        )}
                    </Typography>
                )}

                {sections.map((section) => (
                    <div
                        className={`d-flex justify-content-between align-items-center mb-2 mt-4 rounded p-2 ${classes.hit}`}
                        key={section.sectionId}
                        onClick={() => onOpenSection(section)}
                        style={CARD_STYLE}
                    >
                        <div style={CARD_TEXT_STYLE}>
                            <span style={CRUMB_STYLE} title={sectionPath(section)}>
                                {highlightPath(sectionPath(section), query)}
                            </span>
                            <KeywordPreview
                                headline={section.headline ?? section.title}
                                style={typographyStyles.paragraphStyle}
                            />
                        </div>
                        <div style={CARD_ACTIONS_STYLE}>
                            <ActionIconButton
                                id={`sd-open-${section.sectionId}`}
                                label={openLabel}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onOpenSection(section);
                                }}
                            >
                                <Icon color="blueOne" name="open-in-new" />
                            </ActionIconButton>
                            <ActionIconButton
                                id={`sd-share-${section.sectionId}`}
                                label={shareLabel}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    onShareSection(section);
                                }}
                            >
                                <Share color={colors.blueOne} />
                            </ActionIconButton>
                        </div>
                    </div>
                ))}

                {isLoading && sections.length > 0 && skeletonRows(SKELETON_MORE_KEYS)}
            </Modal.Body>

            <Modal.Footer>
                <Button onPress={onClose} style={CANCEL_STYLE} variant="ghost-blue">
                    {t('common:cancel', 'Cancel')}
                </Button>
                <Button onPress={onOpenManual} variant="primary">
                    {t('stisPublications:serviceDiagnostics.openFullManual', 'Open full document')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
